// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Providing and sharing
// ==============================================
// /papers/requests/:rid — what the skipper needs from me (Crew-3). Each paper
// with its state, the "only <skipper> and you" promise, progress, and the way
// on to Share once something is ready. Nothing here is seen by the skipper
// until the crew member shares.
// ==============================================

import { Link, useParams } from 'react-router-dom';
import { useCrewRequest } from '../hooks';
import { apiError } from '../apiError';
import { closedMessage, isAnswered, isSharedNow, isWritable, itemStatus, progress } from '../itemDisplay';
import type { ItemTone } from '../itemDisplay';
import { shareableItems } from '../share';
import { fmtDay, firstName, passageLabel } from '../dates';
import type { CrewItemView } from '../types';
import { C, Chevron, CrewShell, ErrorNote, Loading, PrivateBanner } from '../ui';

const TONE: Record<ItemTone, string> = {
  todo: 'text-[#a3b3cf]', ready: 'text-[#9fcbff]', shared: 'text-[#6ee7a2]', skipped: 'text-[#c7d3e6]', attention: 'text-[#ffc766]', ended: 'text-[#a3b3cf]',
};

export function ItemIcon({ type }: { type: string }) {
  const path = type === 'passport'
    ? <><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></>
    : type === 'emergency_contact'
      ? <><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></>
      : <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5" /></>;
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0f2856] text-[#9fcbff]">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{path}</svg>
    </span>
  );
}

const itemClass = `flex min-h-[72px] items-center gap-3.5 rounded-[14px] border border-[#1d3a70] bg-[#0b1f45] p-4 text-[#e8eef8] no-underline ${C.focus}`;

function ItemRow({ rid, item, requesterName, timezone, writable }: { rid: string; item: CrewItemView; requesterName: string | null; timezone: string; writable: boolean }) {
  const s = itemStatus(item, { requesterName, timezone });
  const title = `${item.label}${item.required ? '' : ' (optional)'}`;
  const href = `/papers/requests/${rid}/items/${item.id}`;
  const text = (
    <span className="flex min-w-0 flex-grow flex-col gap-0.5">
      <span className="text-[17px] font-semibold">{title}</span>
      <span className={`flex items-center gap-1.5 text-sm ${TONE[s.tone]}`}>{s.tone === 'shared' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>}{s.text}</span>
    </span>
  );

  // A paper the crew member may skip (e.g. an NZeTA for NZ citizens) offers both answers here.
  if (writable && item.notNeededAllowed && !isAnswered(item)) {
    return (
      <div role="group" aria-label={item.label} className="flex flex-col gap-3 rounded-[14px] border border-[#1d3a70] bg-[#0b1f45] p-4">
        <div className="flex items-center gap-3.5"><ItemIcon type={item.type} />{text}</div>
        <div className="flex gap-2">
          <Link to={href} className={`flex min-h-[44px] flex-1 items-center justify-center rounded-[10px] border border-[#2a4a86] bg-[#0f2856] text-[15px] font-semibold text-white ${C.focus}`}>Add</Link>
          <Link to={`${href}?answer=not-needed`} className={`flex min-h-[44px] flex-1 items-center justify-center rounded-[10px] border border-[#2a4a86] text-[15px] font-semibold text-[#c7d3e6] ${C.focus}`}>Not needed</Link>
        </div>
      </div>
    );
  }
  return (
    <Link to={href} className={itemClass}>
      <ItemIcon type={item.type} />{text}<Chevron />
    </Link>
  );
}

export function CrewChecklistPage() {
  const { rid = '' } = useParams();
  const q = useCrewRequest(rid);

  if (q.isLoading) return <CrewShell><Loading /></CrewShell>;
  if (q.error || !q.data) {
    const e = apiError(q.error, 'This request could not be loaded');
    return (
      <CrewShell>
        {e.status === 404 ? (
          <div className="flex flex-col gap-3 pt-6">
            <h1 className="m-0 text-[26px] font-bold leading-tight">This request isn’t available</h1>
            <p className={`m-0 text-base leading-relaxed ${C.soft}`}>It may have been sent to a different address, or not sent yet. Open the link in the request email, or ask the skipper to send it again.</p>
          </div>
        ) : <ErrorNote>{e.message}</ErrorNote>}
        <Link to="/papers" className={`min-h-[44px] self-start py-2 text-base ${C.link}`}>Go to my papers</Link>
      </CrewShell>
    );
  }

  const { request: req, me, items } = q.data;
  const who = firstName(req.requesterName);
  const writable = isWritable(req);
  const { ready, total } = progress(items);
  const toShare = writable ? shareableItems(items) : [];
  const anyShared = items.some(isSharedNow);
  const passage = passageLabel(req.fromPlace, req.toPlace);
  const closed = closedMessage(req);

  return (
    <CrewShell right={me.name ?? me.email}>
      <div className="flex flex-col gap-1.5">
        <span className={C.label}>For {req.requesterName ?? 'your skipper'}{passage ? ` · ${passage}` : ''}</span>
        <h1 className="m-0 text-[26px] font-bold leading-tight">{total} paper{total === 1 ? '' : 's'}{req.dueOn ? `, due ${fmtDay(req.dueOn)}` : ''}</h1>
      </div>

      <PrivateBanner who={who} />
      {closed && <p role="status" className={`m-0 rounded-xl border border-[#1d3a70] px-3.5 py-3 text-[15px] leading-relaxed ${C.soft}`}>{closed}</p>}

      <div className="flex flex-col gap-2.5">
        {items.map((i) => <ItemRow key={i.id} rid={rid} item={i} requesterName={req.requesterName} timezone={req.timezone} writable={writable} />)}
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-2">
        {toShare.length > 0 && (
          <Link to={`/papers/requests/${rid}/share`} className={`flex min-h-[52px] items-center justify-center rounded-xl bg-[#2563eb] text-[17px] font-bold text-white ${C.focus}`}>Share with {who}</Link>
        )}
        {toShare.length === 0 && anyShared && (
          <Link to={`/papers/requests/${rid}/shared`} className={`flex min-h-[52px] items-center justify-center rounded-xl border border-[#2a4a86] text-[17px] font-bold text-white ${C.focus}`}>See what you shared</Link>
        )}
        <div className="h-2 overflow-hidden rounded bg-[#0f2856]" role="progressbar" aria-label="Papers ready" aria-valuemin={0} aria-valuemax={total} aria-valuenow={ready}>
          <div className="h-2 bg-[#34d27a]" style={{ width: `${total ? Math.round((ready / total) * 100) : 0}%` }} />
        </div>
        <div className={`flex justify-between gap-3 text-sm ${C.muted}`}><span>{ready} of {total} ready</span><span>You can stop and come back</span></div>
      </div>
    </CrewShell>
  );
}

export default CrewChecklistPage;
